import { z } from 'zod';
import { PreferredLanguageSchema, type PreferredLanguage } from './api-agent-runtime.js';

// Setter spec §09.9–§09.11 — LIA's voice on calls, per project: provider voice, speaking rate, bilingual switching and
// barge-in (interruption) thresholds. Voice is pre-Phase 0: none of this is verified with a real call yet, so every
// screen shows it «sin verificar» (the provider voice, accent, latency to stop speaking and noise rejection all need a
// listening test). The tuned audio defaults of the voice service stay in force while a value is left empty (null).

/** Provider voice id (Cartesia voice UUID or similar): letters, digits and `-_.:` only. */
const VoiceId = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[\w.:-]+$/, 'Id de voz inválido');

/** Speaking-rate bounds accepted from the platform (the voice service clamps again). 1.0 = provider normal speed. */
export const VOICE_SPEAKING_RATE_MIN = 0.8;
export const VOICE_SPEAKING_RATE_MAX = 1.2;

export const VoiceBargeInSchema = z.object({
  /** Off = LIA finishes her sentence even if the prospect talks over her (not recommended; §09.11). */
  enabled: z.boolean().default(true),
  /**
   * Recognised words (backchannels such as «ajá», «sí», «mmm» do not count) needed before LIA stops talking.
   * The pipeline has no separate VAD: speech start comes from the STT (Deepgram Flux), so this and `minSpeechMs` are
   * the thresholds that separate a real interruption from noise, echo or brief sounds.
   */
  minWords: z.number().int().min(1).max(6).default(1),
  /** Minimum time the prospect must have been speaking (interim transcripts) before LIA stops; a final transcript
   *  with enough words always counts. */
  minSpeechMs: z.number().int().min(0).max(2000).default(300),
});
export type VoiceBargeIn = z.infer<typeof VoiceBargeInSchema>;

export const VOICE_BARGE_IN_DEFAULTS: VoiceBargeIn = { enabled: true, minWords: 1, minSpeechMs: 300 };

export const VoicePersonaConfigSchema = z.object({
  /** null = the voice service's configured voice (CARTESIA_VOICE_ID; the female, Mexican-neutral target of §09.10). */
  voiceId: VoiceId.nullable().default(null),
  /** Voice used once the call switches to English; null = the same voice speaking English. */
  englishVoiceId: VoiceId.nullable().default(null),
  /** null = the voice service's tuned speed. */
  speakingRate: z.number().min(VOICE_SPEAKING_RATE_MIN).max(VOICE_SPEAKING_RATE_MAX).nullable().default(null),
  /** §09.9: Spanish by default, switch to English when the prospect speaks it. Off = always Spanish. */
  bilingual: z.boolean().default(true),
  /** Words in the other language a single turn needs to switch (shorter turns need two in a row). */
  languageSwitchMinWords: z.number().int().min(1).max(10).default(3),
  bargeIn: VoiceBargeInSchema.default(VOICE_BARGE_IN_DEFAULTS),
});
export type VoicePersonaConfig = z.infer<typeof VoicePersonaConfigSchema>;
export type VoicePersonaConfigInput = z.input<typeof VoicePersonaConfigSchema>;

export const VOICE_PERSONA_DEFAULTS: VoicePersonaConfig = VoicePersonaConfigSchema.parse({});

export const VoicePersonaSchema = z.object({
  projectId: z.string(),
  config: VoicePersonaConfigSchema,
  defaults: VoicePersonaConfigSchema,
  /** Target identity of §09.10 (not a claim that the provider voice delivers it). */
  persona: z.object({ gender: z.literal('female'), accent: z.literal('es-MX neutral'), label: z.string() }),
  /** Always «sin verificar» until real calls are reviewed (Phase 0); never derived from saving a value. */
  verification: z.object({ state: z.literal('unverified'), notes: z.array(z.string()) }),
  updatedAt: z.string().nullable(),
});
export type VoicePersona = z.infer<typeof VoicePersonaSchema>;

export const PutVoicePersonaSchema = VoicePersonaConfigSchema;

/** Session language the voice service starts in (bootstrap.language). */
export const VOICE_SESSION_LANGUAGE: Record<PreferredLanguage, 'es-MX' | 'en-US'> = { es: 'es-MX', en: 'en-US' };

/** Voice block of the session bootstrap (docs/voice-service.md). */
export const VoiceSessionVoiceSchema = z.object({
  persona: z.literal('female_mx_neutral'),
  voiceId: z.string().nullable(),
  englishVoiceId: z.string().nullable(),
  speakingRate: z.number().nullable(),
  bilingual: z.boolean(),
  languageSwitchMinWords: z.number().int(),
  bargeIn: VoiceBargeInSchema,
  /** Where the starting language came from: the contact's stored preference or the Spanish default. */
  languageSource: z.enum(['default', 'contact']),
});
export type VoiceSessionVoice = z.infer<typeof VoiceSessionVoiceSchema>;

/** Voice-service event: the call switched language (detected from speech or explicitly asked for). */
export const VoiceLanguageChangedFields = {
  language: PreferredLanguageSchema,
  previous: PreferredLanguageSchema.nullable().optional(),
  source: z.enum(['detected', 'explicit']),
};

export const VOICE_PERSONA_LABEL = 'Voz femenina, natural y profesional, acento mexicano neutro';
export const VOICE_PERSONA_UNVERIFIED_NOTES = [
  'Voz, acento y naturalidad sin verificar: requieren una prueba de escucha con llamadas reales o simuladas (§09.10).',
  'Interrupciones (barge-in) sin verificar: latencia para dejar de hablar y rechazo de ruido/eco pendientes de prueba (§09.11).',
  'Cambio a inglés sin verificar: calidad real de reconocimiento y voz en español mexicano e inglés pendiente (§09.9).',
];
